import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, Phone, Mail, Calendar, FileText, Upload, 
  CheckCircle, Loader2, AlertCircle, Building2 
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

// Default branding for EPP
const DEFAULT_BRANDING = {
  organization_name: "Elite Player Performance",
  primary_color: "#02b5ff",
  secondary_color: "#1a1a2e",
  accent_color: "#ff025f",
  logo_url: null
};

export default function ReferralPage() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(!!slug);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referralId, setReferralId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    date_of_birth: '',
    reason_for_referral: '',
    referrer_name: '',
    referrer_organization: '',
    referrer_email: '',
    referrer_phone: '',
    additional_notes: ''
  });
  
  const [documents, setDocuments] = useState([]);

  // Fetch partner branding if slug provided
  useEffect(() => {
    const fetchBrand = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/referrals/partner-info/${slug}`);
        setBrand({
          ...DEFAULT_BRANDING,
          ...response.data
        });
      } catch (err) {
        console.error('Partner not found:', err);
        toast.error('Partner organization not found. Showing default form.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBrand();
  }, [slug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please enter the full name');
      return;
    }
    if (!formData.phone) {
      toast.error('Phone number is required');
      return;
    }
    if (!formData.email) {
      toast.error('Email address is required');
      return;
    }
    if (!formData.date_of_birth) {
      toast.error('Date of birth is required');
      return;
    }
    if (!formData.reason_for_referral) {
      toast.error('Reason for referral is required');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const endpoint = slug 
        ? `${API}/referrals/submit/${slug}`
        : `${API}/referrals/submit`;
      
      const response = await axios.post(endpoint, formData);
      
      setReferralId(response.data.referral_id);
      setSubmitted(true);
      toast.success('Referral submitted successfully!');
    } catch (err) {
      console.error('Referral error:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit referral');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !referralId) return;
    
    setUploadingDoc(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/referrals/${referralId}/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setDocuments(prev => [...prev, response.data.document]);
      toast.success('Document uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Dynamic styles
  const primaryColor = brand.primary_color;
  const secondaryColor = brand.secondary_color;
  const accentColor = brand.accent_color;

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: secondaryColor }}
        data-testid="referral-success"
      >
        <div className="w-full max-w-md text-center">
          {/* Logo */}
          <div className="mb-6">
            {brand.logo_url ? (
              <img 
                src={`${API}${brand.logo_url}`} 
                alt={brand.organization_name}
                className="h-16 mx-auto"
              />
            ) : (
              <div 
                className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {brand.organization_name.charAt(0)}
              </div>
            )}
          </div>
          
          <div 
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <CheckCircle className="h-10 w-10" style={{ color: primaryColor }} />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-4">
            Referral Submitted!
          </h1>
          
          <p className="text-gray-400 mb-6">
            Thank you for your referral to {brand.organization_name}. 
            Confirmation emails have been sent and we will be in touch shortly.
          </p>
          
          <div 
            className="rounded-lg p-4 mb-6"
            style={{ backgroundColor: `${primaryColor}15`, borderLeft: `4px solid ${primaryColor}` }}
          >
            <p className="text-sm text-gray-300">
              Reference ID: <span className="font-mono text-white">{referralId}</span>
            </p>
          </div>
          
          {/* Document upload section */}
          <div className="mt-8 text-left">
            <h3 className="text-white font-semibold mb-3">Upload Supporting Documents (Optional)</h3>
            <p className="text-sm text-gray-400 mb-4">
              You can upload any relevant documents such as medical reports, previous assessments, or other supporting files.
            </p>
            
            <label className="cursor-pointer block">
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={handleDocumentUpload}
                className="hidden"
                disabled={uploadingDoc}
              />
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  uploadingDoc ? 'opacity-50' : 'hover:border-opacity-100'
                }`}
                style={{ borderColor: `${primaryColor}50` }}
              >
                {uploadingDoc ? (
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                ) : (
                  <>
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-400 text-sm">Click to upload document</p>
                    <p className="text-gray-500 text-xs mt-1">PDF, PNG, JPG, DOC (max 10MB)</p>
                  </>
                )}
              </div>
            </label>
            
            {/* Uploaded documents list */}
            {documents.length > 0 && (
              <div className="mt-4 space-y-2">
                {documents.map((doc, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50"
                  >
                    <FileText className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-white flex-1 truncate">{doc.original_name}</span>
                    <CheckCircle className="h-4 w-4" style={{ color: primaryColor }} />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <p className="text-center text-gray-500 text-xs mt-8">
            Powered by <span className="text-gray-400">BE ELITE APP</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{ backgroundColor: secondaryColor }}
      data-testid="referral-page"
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {brand.logo_url ? (
            <img 
              src={`${API}${brand.logo_url}`} 
              alt={brand.organization_name}
              className="h-16 mx-auto mb-4"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: primaryColor }}
            >
              {brand.organization_name.charAt(0)}
            </div>
          )}
          
          <h1 className="text-2xl font-bold text-white">
            Refer a Client to {brand.organization_name}
          </h1>
          
          <p className="text-gray-400 mt-2">
            Complete this form to refer an athlete for performance assessment and training.
          </p>
        </div>

        {/* Form */}
        <form 
          onSubmit={handleSubmit}
          className="rounded-2xl p-6 md:p-8 border"
          style={{ 
            backgroundColor: `${secondaryColor}cc`, 
            borderColor: `${primaryColor}30` 
          }}
        >
          {/* Client Information */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="h-5 w-5" style={{ color: primaryColor }} />
              Client Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 mb-1 block">First Name *</Label>
                <Input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="John"
                  data-testid="referral-first-name"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-1 block">Last Name *</Label>
                <Input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="Smith"
                  data-testid="referral-last-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-gray-300 mb-1 block">
                  <Phone className="h-4 w-4 inline mr-1" /> Phone Number *
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="07123 456789"
                  data-testid="referral-phone"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-1 block">
                  <Mail className="h-4 w-4 inline mr-1" /> Email Address *
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="john@example.com"
                  data-testid="referral-email"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <Label className="text-gray-300 mb-1 block">
                <Calendar className="h-4 w-4 inline mr-1" /> Date of Birth *
              </Label>
              <Input
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleChange}
                required
                className="bg-black/30 border-zinc-700 text-white"
                style={{ borderColor: `${primaryColor}40` }}
                data-testid="referral-dob"
              />
            </div>
          </div>

          {/* Reason for Referral */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: primaryColor }} />
              Reason for Referral *
            </h2>
            
            <textarea
              name="reason_for_referral"
              value={formData.reason_for_referral}
              onChange={handleChange}
              required
              rows={4}
              className="w-full bg-black/30 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
              style={{ borderColor: `${primaryColor}40` }}
              placeholder="Please describe the reason for this referral, including any specific goals, concerns, or conditions..."
              data-testid="referral-reason"
            />
          </div>

          {/* Referrer Information */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5" style={{ color: primaryColor }} />
              Your Information (Referrer)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 mb-1 block">Your Name</Label>
                <Input
                  name="referrer_name"
                  value={formData.referrer_name}
                  onChange={handleChange}
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="Dr. Jane Doe"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-1 block">Organization</Label>
                <Input
                  name="referrer_organization"
                  value={formData.referrer_organization}
                  onChange={handleChange}
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="ABC Sports Clinic"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-gray-300 mb-1 block">Your Email (for confirmation)</Label>
                <Input
                  name="referrer_email"
                  type="email"
                  value={formData.referrer_email}
                  onChange={handleChange}
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="jane@clinic.com"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-1 block">Your Phone</Label>
                <Input
                  name="referrer_phone"
                  type="tel"
                  value={formData.referrer_phone}
                  onChange={handleChange}
                  className="bg-black/30 border-zinc-700 text-white"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="01onal234 567890"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="mb-8">
            <Label className="text-gray-300 mb-1 block">Additional Notes</Label>
            <textarea
              name="additional_notes"
              value={formData.additional_notes}
              onChange={handleChange}
              rows={3}
              className="w-full bg-black/30 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
              style={{ borderColor: `${primaryColor}40` }}
              placeholder="Any additional information that might be helpful..."
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-white font-semibold rounded-xl"
            style={{ backgroundColor: primaryColor }}
            data-testid="referral-submit"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Submitting Referral...
              </span>
            ) : (
              'Submit Referral'
            )}
          </Button>
          
          <p className="text-center text-gray-500 text-xs mt-4">
            By submitting this form, you confirm that you have the client's consent to share their information.
          </p>
        </form>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-8">
          Powered by <span className="text-gray-400">BE ELITE APP</span>
        </p>
      </div>
    </div>
  );
}
